import React from 'react';

type Fingerprint = {
  rows: number;
  columns: string[] | { names?: string[] };
  columnTypes?: Record<string, string>;
  missing?: Record<string, number>;
  statistics?: {
    numerical?: Record<string, { mean: number; std: number; min: number; max: number }>;
    categorical?: Record<string, { unique: number; mode: string }>;
  } & { warnings?: string[] };
  patterns?: {
    hasTimeSeries?: boolean;
    timeColumn?: string | null;
    hasGroups?: boolean;
    groupColumns?: string[];
    correlations?: Array<{ col1: string; col2: string; value: number }> | number;
  };
  suggestions?: { plotTypes?: string[] };
};

export function FingerprintSummaryCard({ fp }: { fp: Fingerprint }) {
  const columns = Array.isArray((fp as any)?.columns)
    ? ((fp as any).columns as string[])
    : Array.isArray((fp as any)?.columns?.names)
      ? ((fp as any).columns.names as string[])
      : [];
  const rows = (fp as any)?.rows || 0;

  // Missingness summary
  const missing = (fp as any)?.missing || {} as Record<string, number>;
  const colsWithMissing = Object.entries(missing)
    .filter(([, c]) => (Number(c) || 0) > 0)
    .sort((a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0));
  const topMissing = colsWithMissing.slice(0, 3).map(([k, c]) => ({ k, c }));

  // Patterns/notes
  const hasTS = !!(fp as any)?.patterns?.hasTimeSeries;
  const tsCol = (fp as any)?.patterns?.timeColumn || null;
  // Hide groups per request
  const corrField = (fp as any)?.patterns?.correlations;
  const corrCount = Array.isArray(corrField) ? corrField.length : (typeof corrField === 'number' ? corrField : 0);
  const topCorr = Array.isArray(corrField)
    ? corrField
        .slice(0, 2)
        .sort((a, b) => Math.abs((b?.value || 0)) - Math.abs((a?.value || 0)))
        .map(c => `${c.col1}↔${c.col2} (${Math.round(Math.abs(c.value || 0)*100)/100})`)
    : [];

  // Column types summary
  const ct = ((fp as any)?.columnTypes || {}) as Record<string, string>;
  const typeCounts = Object.values(ct).reduce<Record<string, number>>((acc, t) => {
    const key = String(t);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const sampleCols = columns.slice(0, 8);

  return (
    <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 text-sm" data-testid="fp-summary-card" data-lumi="mapping-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="font-medium">Data Summary</div>
        <div className="text-xs text-muted-foreground">{rows} rows × {columns.length} columns</div>
      </div>
      {sampleCols.length > 0 && (
        <div className="text-xs text-muted-foreground mb-1 break-words leading-snug">
          <span className="font-medium mr-1">Columns:</span>
          <span className="whitespace-normal break-words">
            {sampleCols.join(', ')}{columns.length > sampleCols.length ? '…' : ''}
          </span>
        </div>
      )}
      <div className="mb-1">
        <div className="text-xs">
          <div className="font-medium mb-0.5">Types</div>
          <div className="text-muted-foreground space-y-0.5 break-words leading-snug">
            {Object.keys(typeCounts).length === 0 ? '-' : (
              Object.entries(typeCounts).map(([k, v]) => (
                <div key={k}>{k}: {v}</div>
              ))
            )}
          </div>
        </div>
        <div className="text-xs mt-1">
          <div className="font-medium mb-0.5">Missing</div>
          <div className="text-muted-foreground space-y-0.5 break-words leading-snug">
            {(() => {
              if (Object.keys(missing).length === 0) return 'No missing values';
              if (colsWithMissing.length === 0) return 'No missing values';
              return topMissing.map(({ k, c }) => (
                <div key={k}>{k}: {rows > 0 ? Math.round((Number(c) / rows) * 100) : 0}%</div>
              ));
            })()}
          </div>
        </div>
      </div>
      <div className="text-xs">
        <div className="font-medium mb-0.5">Notes</div>
        <ul className="list-disc ml-4 text-muted-foreground leading-snug space-y-0.5">
          {hasTS && <li>Time series detected{tsCol ? ` (${tsCol})` : ''}</li>}
          {/* groups removed from card */}
          {Number.isFinite(corrCount) && corrCount > 0 && (<li>{corrCount} correlations</li>)}
          {(() => {
            try {
              const c = Number(((fp as any)?.patterns?.lowSampleColumnsCount ?? (fp as any)?.lowSampleColumnsCount) || 0);
              return <li>Low sample size (N&lt;30) columns: {isFinite(c) ? c : 0}</li>;
            } catch { return <li>Low sample size (N&lt;30) columns: 0</li>; }
          })()}
          {(() => {
            try {
              const num = Object.entries((fp as any)?.columnTypes || {}).filter(([_, t]) => String(t)==='numeric').map(([k])=>k);
              const stats = ((fp as any)?.statistics?.numerical || {}) as Record<string, any>;
              let normal = 0; let nonNormal = 0;
              for (const col of num) {
                const s = stats[col];
                if (!s) continue;
                if (s.isNormal === true) normal += 1; else if (s.isNormal === false) nonNormal += 1;
              }
              if (normal + nonNormal > 0) {
                return <li>Normality: {normal} normal • {nonNormal} non-normal</li>;
              }
            } catch {}
            return null;
          })()}
          {/* removed duplicate low sample warning */}
          {(fp as any)?.statistics?.warnings?.includes('zero_variance') && <li>Zero variance detected in some columns</li>}
          {(!hasTS && corrCount === 0 && !((fp as any)?.statistics?.warnings || []).length) && <li>No notable patterns detected</li>}
        </ul>
      </div>
    </div>
  );
}

export default FingerprintSummaryCard;


